import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RbacPermissions } from '../../../core/interfaces/user';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import { HdRbacIconButton } from '../../../components';
import HdButton from '../../../button/components/button/HdButton';
import HdIcon from '../../../media/components/icon/HdIcon';
import { HdTooltip } from '../../../tooltip/HdTooltip';

export default function NameEditor({
  dataId,
  className = '',
  getNameUpdatePromise,
  name,
  setName,
  disabled,
  saveSuccess,
  editStart,
  editEnd,
  onCancel,
  rbacPermission
}: {
  dataId: string;
  className?: string;
  name: string;
  setName: (name: string) => any;
  disabled?: boolean;
  getNameUpdatePromise: (name) => Promise<any>;
  saveSuccess?: Function;
  editStart?: Function;
  editEnd?: Function;
  onCancel: Function;
  rbacPermission?: RbacPermissions;
}) {
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const saveDisabled = saving || !name.trim().length;

  const focus = function () {
    if (disabled) {
      return;
    }

    setEditMode(true);

    setTimeout(() => {
      inputRef.current.focus();
    }, 0);
  };

  const onIpFocus = function () {
    if (typeof editStart === 'function') {
      editStart();
    }
  };

  const onIpBlur = function () {
    if (typeof editEnd === 'function') {
      editEnd();
    }
  };

  const onKeydown = function (e: any) {
    if (e.keyCode === 13 && !saveDisabled) {
      save();
    }
  };

  const save = function () {
    setSaving(true);

    getNameUpdatePromise(name).then(
      (res: any) => {
        setSaving(false);
        setEditMode(false);

        if (typeof saveSuccess === 'function') {
          saveSuccess(name);
        }
      },
      () => {
        setSaving(false);
      }
    );
  };

  const cancel = function () {
    setEditMode(false);
    onCancel();
  };

  const dataIdGenerator = useCallback(getDataIdGenerator(dataId, 'nameEditor'), [dataId]);

  return (
    <div className={clsx('name-editor', className)}>
      <div className='name-editor-wrapper'>
        <HdTooltip title={name} disabled={!!editMode} enableOnTextOverflow>
          <div
            className={`name-editor-name-holder text-ellipsis ${
              !editMode ? 'name-editor-name-holder--visible' : ''
            }`}
            onClick={() => {
              focus();
            }}
          >
            {name}
          </div>
        </HdTooltip>

        {!editMode && !disabled && (
          <div className='center-flex-row ml-1'>
            <HdRbacIconButton
              rbacPermission={rbacPermission}
              verticalEdge='both'
              horizontalEdge='both'
              onClick={() => {
                focus();
              }}
              dataId={dataIdGenerator('edit')}
            >
              <HdIcon name='edit' size={2} />
            </HdRbacIconButton>
          </div>
        )}

        <input
          ref={inputRef}
          className={`name-editor-input ${editMode ? 'name-editor-input--visible' : ''}`}
          type='text'
          value={name}
          onChange={e => {
            setName(e.target.value);
          }}
          onFocus={() => {
            onIpFocus();
          }}
          onBlur={() => {
            onIpBlur();
          }}
          onKeyDown={e => {
            onKeydown(e);
          }}
          tabIndex={editMode ? 0 : -1}
          data-id={dataIdGenerator('input')}
        />

        {editMode && (
          <>
            <HdButton
              size='sm'
              className='ml-4'
              disabled={saveDisabled}
              onClick={() => save()}
              dataId={dataIdGenerator('save')}
            >
              Save
            </HdButton>

            <HdButton
              size='sm'
              variation='outline'
              className='ml-1'
              onClick={() => cancel()}
              dataId={dataIdGenerator('cancel')}
            >
              Cancel
            </HdButton>
          </>
        )}
      </div>
    </div>
  );
}
